import { useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { useSelector, useDispatch } from "react-redux";
import { ExitDialog } from "../ExitDialog";
import { Bank } from "../Bank";
import { Header } from "../Header";
import { Footer } from "../Footer";
import {
  answerQuestion,
  finishEntryAnimation,
  finishGame,
  incrementTime,
  setAnimating,
} from "@educaplay/store/slices/gameSlice";
import {
  useGameSounds,
  useGameAnimations,
  ProgressBar,
  VisualFeedback,
} from "@educaplay/core";
import {
  NAME_SOUND_SUCCESS,
  NAME_SOUND_FAILURE,
  NAME_SOUND_LIVE,
  NAME_ANIMATION_LIVES,
  NAME_ANIMATION_POINTS,
} from "@educaplay/core/utils";
import { GameLayout } from "../GameLayout";
import { SCREENS } from "@educaplay/store/constants";
import { useState } from "react";
import { FeedbackDialog } from "../FeedbackDialog";
import { isUnansweredAnswer } from "@educaplay/store/utils/functions";
import classes from "./GameProviderContent.module.scss";
import { AnimatePresence } from "framer-motion";

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const GameProviderContent = forwardRef(function GameProviderContent(
  props,
  ref
) {
  const { Game, hasCustomLayout, classes: customClasses } = props;

  const visualFeedbackRef = useRef(null);
  const gameRef = useRef(null);
  const headerRef = useRef(null);
  const footerRef = useRef(null);
  const bankRef = useRef(null);

  const hasBank = useSelector((state) => {
    if (state.game.isBankHidden) return false;

    if (state.game.hasQuestionBank) {
      const question = state.game.questions[state.game.currentIndex];
      return !!question?.bank;
    }
    return state.game.hasBank;
  });

  const questions = useSelector((state) => state.game.questions);
  const currentIndex = useSelector((state) => state.game.currentIndex);
  const liveActived = useSelector((state) => state.game.liveActived);
  const screen = useSelector((state) => state.game.view);
  const countFooterSpace = useSelector((state) => state.game.countFooterSpace);
  const showFeedbackGame = useSelector((state) => state.game.showFeedbackGame);
  const semiCorrect = useSelector((state) => state.game.semiCorrect);
  const hasProgressBar = useSelector((state) => state.game.hasProgressBar);
  const isExamMode = useSelector((state) => state.game.examMode);
  const [feedbackQuestionIndex, setFeedbackQuestionIndex] = useState(null);

  const answeredQuestions = questions.filter((question) =>
    hasProgressBar.onlyCorrect
      ? !isUnansweredAnswer(question.answer, { examMode: isExamMode }) &&
        question?.checkAnswer?.(question.answer)
      : !isUnansweredAnswer(question.answer, { examMode: isExamMode })
  );
  const progressPercent = (answeredQuestions.length / questions.length) * 100;

  const dispatch = useDispatch();
  const gameSounds = useGameSounds();
  const gameAnimations = useGameAnimations();
  const [isInitGame, setIsInitGame] = useState(false);

  const [answerData, setAnswerData] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);

  useImperativeHandle(ref, () => ({
    animateExit: async () => {
      await footerRef.current.animateExit();
      bankRef.current.animateExit();
      await gameRef.current?.animateExit?.();
      await headerRef.current.animateExit({ delay: 0.3 });
    },
  }));

  const handleAnswer = async (answer, params = {}) => {
    // NOTE: Phaser already blocks multiple clicks with its own check (this.moving || this.finish)
    // so we don't need to check isAnimating here - it would cause false positives
    dispatch(setAnimating(true));
    const currentQuestion =
      questions[params.questionIndexAnswer ?? currentIndex];

    const isCorrect = currentQuestion?.checkAnswer?.(answer) ?? false;
    const hasCustomPoints =
      typeof currentQuestion?.calculatePoints === "function";
    const points = hasCustomPoints
      ? currentQuestion.calculatePoints(answer, currentQuestion)
      : currentQuestion?.points ?? 0;

    const playErrorSound = params.playErrorSound ?? true;

    await wait(400);
    if (isCorrect) {
      gameSounds.play(NAME_SOUND_SUCCESS);
      gameAnimations.start(NAME_ANIMATION_POINTS, { value: points });
    } else {
      if (semiCorrect && points > 0) {
        gameSounds.play(NAME_SOUND_SUCCESS);
        gameAnimations.start(NAME_ANIMATION_POINTS, { value: points });
      }
      if (playErrorSound) gameSounds.play(NAME_SOUND_FAILURE);

      if (liveActived) {
        setTimeout(() => {
          gameAnimations.start(NAME_ANIMATION_LIVES);
          gameSounds.play(NAME_SOUND_LIVE);
        }, params.delayLiveAnimation ?? 0);
      }
    }

    const executeVisualFeedback = async () => {
      const defaultDuration = isCorrect ? 100 : 200;
      const duration = params.feedbackDuration ?? defaultDuration;
      await visualFeedbackRef.current?.show({ isCorrect, duration });
    };

    if (params.showFeedback) {
      await executeVisualFeedback();
    } else if (!isCorrect && liveActived) {
      await executeVisualFeedback();
    }

    const formattedAnswerData = {
      answer,
      isCorrect,
      points,
      semiCorrect,
      nextQuestionIndex: params.nextQuestionIndex,
      questionIndexAnswer: params.questionIndexAnswer,
    };

    await wait(200);

    if (
      currentQuestion?.feedback &&
      ((showFeedbackGame?.isCorrect && isCorrect) ||
        (showFeedbackGame?.isError && !isCorrect))
    ) {
      setShowFeedback(true);
      setAnswerData(formattedAnswerData);
      setFeedbackQuestionIndex(params.questionIndexAnswer ?? currentIndex);
      return;
    }

    dispatch(setAnimating(false));
    dispatch(answerQuestion(formattedAnswerData));
  };

  const onCloseFeedbackHandler = () => {
    dispatch(setAnimating(false));
    dispatch(
      answerQuestion({
        ...answerData,
        questionIndexAnswer: feedbackQuestionIndex,
      })
    );
    setAnswerData(null);
    setShowFeedback(false);
    setFeedbackQuestionIndex(null);
  };

  const handleTimeFinish = async () => {
    dispatch(setAnimating(true));
    gameSounds.play(NAME_SOUND_FAILURE);

    const {
      data: answer = "",
      isCorrect = false,
      points = 0,
      nextQuestionIndex,
      shouldFinish = false,
    } = gameRef.current?.getUserAnswer?.() ?? {};

    await visualFeedbackRef.current?.show({ isCorrect, duration: 200 });
    await wait(200);
    await gameRef.current?.animateFail?.();

    await wait(200);
    if (!isCorrect && liveActived) {
      gameAnimations.start(NAME_ANIMATION_LIVES);
      gameSounds.play(NAME_SOUND_LIVE);
    }
    await wait(400);

    dispatch(setAnimating(false));

    if (shouldFinish) {
      dispatch(finishGame());
      return;
    }

    dispatch(answerQuestion({ answer, isCorrect, points, semiCorrect, nextQuestionIndex }));
  };

  useEffect(() => {
    if (screen !== SCREENS.GAME) return;

    const initAnimation = async () => {
      setIsInitGame(false);
      dispatch(setAnimating(true));

      await headerRef.current.animateEntry();
      await gameRef.current.animateEntry();
      bankRef.current.animateEntry();
      await footerRef.current.animateEntry();
      setIsInitGame(true);
      dispatch(finishEntryAnimation());
      dispatch(setAnimating(false));
    };
    initAnimation();
  }, [dispatch, screen]);

  useEffect(() => {
    if (screen !== SCREENS.GAME || !isInitGame) return;

    const interval = setInterval(() => {
      dispatch(incrementTime());
    }, 1000);
    return () => clearInterval(interval);
  }, [screen, dispatch, isInitGame]);

  return (
    <GameLayout
      hasBank={hasBank}
      countFooterSpace={countFooterSpace}
      hasCustomLayout={hasCustomLayout}
      className={customClasses?.layout}
    >
      <ExitDialog />
      <Header animated ref={headerRef} />
      <AnimatePresence>
        {hasProgressBar.view && screen === SCREENS.GAME && (
          <ProgressBar
            className={classes.progressBarPosition}
            value={progressPercent}
          />
        )}
      </AnimatePresence>
      <Bank hasBank={hasBank} ref={bankRef} className={customClasses?.bank} />

      <FeedbackDialog
        open={showFeedback}
        onClose={onCloseFeedbackHandler}
        question={questions[feedbackQuestionIndex] ?? questions[currentIndex]}
      />
      <Game ref={gameRef} onAnswer={handleAnswer} />

      <VisualFeedback ref={visualFeedbackRef} />
      <Footer
        animated
        ref={footerRef}
        showClock
        clockFreezed={screen !== SCREENS.GAME}
        onTimeFinish={handleTimeFinish}
      />
    </GameLayout>
  );
});
